import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import AdminLayout from "@/components/AdminLayout";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import Customers from "@/pages/Customers";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import CreateInvoice from "@/pages/CreateInvoice";
import EditInvoice from "@/pages/EditInvoice";
import PrintInvoice from "@/pages/PrintInvoice";
import PrintWholesaleInvoice from "@/pages/PrintWholesaleInvoice";
import PrintShipment from "@/pages/PrintShipment";
import TaxReports from "@/pages/TaxReports";
import Financials from "@/pages/Financials";
import BulkImport from "@/pages/BulkImport";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Support from "@/pages/Support";
import Settings from "@/pages/Settings";
import Payments from "@/pages/Payments";
import Profile from "@/pages/Profile";
import Landing from "@/pages/Landing";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBusinesses from "@/pages/admin/AdminBusinesses";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCategories from "@/pages/admin/AdminCategories";
import PublicPayInvoice from "@/pages/PublicPayInvoice";
import Demo from "@/pages/Demo";
import Demos from "@/pages/Demos";
import DemoPlayer from "@/components/demo/DemoPlayer";
import GroceryDashboard from "@/pages/GroceryDashboard";
import WholesaleDashboard from "@/pages/WholesaleDashboard";
import PressureWashingDashboard from "@/pages/PressureWashingDashboard";

// Protected Route wrapper - requires authentication
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Admin Route wrapper - requires super admin role
function AdminRoute({ children }) {
  const { isAuthenticated, isSuperAdmin } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Business Route wrapper - requires authentication and valid business
function BusinessRoute({ children }) {
  const { isAuthenticated, user } = useAuth();
  const { businessSlug } = useParams();
  const routerLocation = useLocation();
  const location = routerLocation.pathname;
  const [businessCategory, setBusinessCategory] = useState(null);
  const [categoryLoaded, setCategoryLoaded] = useState(false);
  
  // Fetch business category for proper routing
  useEffect(() => {
    const fetchBusinessCategory = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
        const response = await fetch(`${API}/b/${businessSlug}/info`);
        if (response.ok) {
          const data = await response.json();
          setBusinessCategory(data.category);
        }
      } catch (error) {
        console.error("Failed to fetch business category");
      }
      setCategoryLoaded(true);
    };
    
    if (businessSlug) {
      fetchBusinessCategory();
    }
  }, [businessSlug]);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if this is the base business route (mechanic dashboard)
  const isAtBaseBusinessRoute = location === `/${businessSlug}` || location === `/${businessSlug}/`;
  // Settings, Profile, Financials, Payments, and Tax Reports are SHARED
  // pages used by every category — they live under /:businessSlug/* so
  // they technically match the mechanic layout's child routes, but we
  // must NOT bounce wholesale/grocery users away from them, otherwise
  // clicking those items silently navigates back to the dashboard.
  const isSharedSubRoute =
    location.startsWith(`/${businessSlug}/settings`) ||
    location.startsWith(`/${businessSlug}/profile`) ||
    location.startsWith(`/${businessSlug}/financials`) ||
    location.startsWith(`/${businessSlug}/payments`) ||
    location.startsWith(`/${businessSlug}/reports`);
  const isAtMechanicSubRoute = location.startsWith(`/${businessSlug}/`) &&
    !location.includes('/wholesale') &&
    !location.includes('/grocery') &&
    !location.includes('/pay/') &&
    !isSharedSubRoute;
  
  // Get effective category - use business category for super admins, user category for regular users
  const effectiveCategory = user?.role === "super_admin" ? businessCategory : user?.business_category;
  
  // WHOLESALE BUSINESS: Block mechanic dashboard completely - redirect immediately
  if (effectiveCategory === 'wholesale') {
    if (isAtBaseBusinessRoute || isAtMechanicSubRoute) {
      return <Navigate to={`/${businessSlug}/wholesale`} replace />;
    }
  }
  
  // GROCERY BUSINESS: Block mechanic dashboard completely - redirect immediately  
  if (effectiveCategory === 'grocery_store') {
    if (isAtBaseBusinessRoute || isAtMechanicSubRoute) {
      return <Navigate to={`/${businessSlug}/grocery`} replace />;
    }
  }
  
  // Super admin can access any business (after category redirect check)
  if (user?.role === "super_admin") {
    return children;
  }
  
  // Regular users can only access their own business
  if (user?.business_slug !== businessSlug) {
    if (user?.business_category === "grocery_store") {
      return <Navigate to={`/${user?.business_slug}/grocery`} replace />;
    }
    if (user?.business_category === "wholesale") {
      return <Navigate to={`/${user?.business_slug}/wholesale`} replace />;
    }
    if (user?.business_category === "pressure_washing") {
      return <Navigate to={`/${user?.business_slug}/pressure-washing`} replace />;
    }
    return <Navigate to={`/${user?.business_slug}`} replace />;
  }
  
  return children;
}

// Public Route wrapper (redirects to appropriate dashboard if logged in)
function PublicRoute({ children, allowRedirect = true }) {
  const { isAuthenticated, isSuperAdmin, businessSlug, user } = useAuth();
  
  if (isAuthenticated && allowRedirect) {
    if (isSuperAdmin) {
      return <Navigate to="/admin" replace />;
    }
    if (businessSlug) {
      // Redirect to category-specific dashboard
      if (user?.business_category === "grocery_store") {
        return <Navigate to={`/${businessSlug}/grocery`} replace />;
      }
      if (user?.business_category === "wholesale") {
        return <Navigate to={`/${businessSlug}/wholesale`} replace />;
      }
      if (user?.business_category === "pressure_washing") {
        return <Navigate to={`/${businessSlug}/pressure-washing`} replace />;
      }
      return <Navigate to={`/${businessSlug}`} replace />;
    }
  }
  
  return children;
}

// Redirect old invoice links to the public pay page
function InvoiceRedirect() {
  const { businessSlug, id } = useParams();
  return <Navigate to={`/${businessSlug}/pay/${id}`} replace />;
}

// Redirect component for authenticated users - show Landing for new visitors
function AuthRedirect() {
  const { isAuthenticated, isSuperAdmin, businessSlug, user } = useAuth();
  
  if (!isAuthenticated) {
    // Show landing page for new visitors
    return <Landing />;
  }
  
  if (isSuperAdmin) {
    return <Navigate to="/admin" replace />;
  }
  
  if (businessSlug) {
    // Redirect to category-specific dashboard
    if (user?.business_category === "grocery_store") {
      return <Navigate to={`/${businessSlug}/grocery`} replace />;
    }
    if (user?.business_category === "wholesale") {
      return <Navigate to={`/${businessSlug}/wholesale`} replace />;
    }
    if (user?.business_category === "pressure_washing") {
      return <Navigate to={`/${businessSlug}/pressure-washing`} replace />;
    }
    return <Navigate to={`/${businessSlug}`} replace />;
  }
  
  return <Navigate to="/login" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<AuthRedirect />} />
      <Route path="/login" element={<AuthRedirect />} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/support" element={<Support />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/demos" element={<Demos />} />
      <Route path="/demo/play/:category" element={<DemoPlayer />} />
      <Route path="/demo/:category" element={<Demo />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="businesses" element={<AdminBusinesses />} />
        <Route path="users" element={<AdminUsers />} />
      </Route>
      
      {/* Wholesale Dashboard - MUST be before general businessSlug routes */}
      <Route path="/:businessSlug/wholesale" element={<BusinessRoute><WholesaleDashboard /></BusinessRoute>} />
      <Route path="/:businessSlug/wholesale/invoices/:id/print" element={<BusinessRoute><PrintWholesaleInvoice /></BusinessRoute>} />
      <Route path="/:businessSlug/wholesale/shipments/:id/print" element={<BusinessRoute><PrintShipment /></BusinessRoute>} />
      
      {/* Grocery Store Dashboard - MUST be before general businessSlug routes */}
      <Route path="/:businessSlug/grocery" element={<BusinessRoute><GroceryDashboard /></BusinessRoute>} />
      <Route path="/:businessSlug/pressure-washing" element={<BusinessRoute><PressureWashingDashboard /></BusinessRoute>} />
      
      {/* Print Invoice - Protected but outside layout */}
      <Route path="/:businessSlug/invoices/:id/print" element={<BusinessRoute><PrintInvoice /></BusinessRoute>} />
      
      {/* Business Routes - :businessSlug/... (General mechanic dashboard) */}
      {/* BusinessRoute wrapper handles redirect for wholesale/grocery users */}
      <Route path="/:businessSlug" element={<BusinessRoute><Layout /></BusinessRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="customers" element={<Customers />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/new" element={<CreateInvoice />} />
        <Route path="invoices/:id" element={<InvoiceDetail />} />
        <Route path="invoices/:id/edit" element={<EditInvoice />} />
        <Route path="reports/tax" element={<TaxReports />} />
        <Route path="financials" element={<Financials />} />
        <Route path="import" element={<BulkImport />} />
        <Route path="settings" element={<Settings />} />
        <Route path="payments" element={<Payments />} />
        <Route path="profile" element={<Profile />} />
      </Route>
      
      {/* Public Pay Invoice - NO authentication required */}
      <Route path="/:businessSlug/pay/:id" element={<PublicPayInvoice />} />
      
      {/* Legacy route redirect - old invoice links go to pay page */}
      <Route path="/:businessSlug/invoices/:id" element={<InvoiceRedirect />} />
      
      {/* Catch all - redirect to welcome */}
      <Route path="*" element={<Navigate to="/welcome" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
